import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Divider,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SensorsIcon from '@mui/icons-material/Sensors';
import SpeedIcon from '@mui/icons-material/Speed';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AssessmentIcon from '@mui/icons-material/Assessment';
import NatureIcon from '@mui/icons-material/Nature';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import CloudIcon from '@mui/icons-material/Cloud';
import SecurityIcon from '@mui/icons-material/Security';

export const ManualTelematik = () => {
  const navigate = useNavigate();

  const telematikFeatures = [
    {
      icon: <LocationOnIcon color="primary" />,
      title: "GPS-Tracking in Echtzeit",
      description: "Präzise Standortbestimmung aller Fahrzeuge für optimierte Disposition"
    },
    {
      icon: <SpeedIcon color="primary" />,
      title: "Fahrstil-Monitoring",
      description: "Analyse von Geschwindigkeit, Beschleunigung und Bremsverhalten"
    },
    {
      icon: <AssessmentIcon color="primary" />,
      title: "Datenanalyse und Reporting",
      description: "Umfassende Berichte über Fahrzeugnutzung und Betriebseffizienz"
    },
    {
      icon: <SecurityIcon color="primary" />,
      title: "Flottenmanagement",
      description: "Zentrale Überwachung und Verwaltung aller Busfahrzeuge"
    }
  ];

  const ecoAppBenefits = [
    "Kraftstoffverbrauch-Optimierung",
    "CO₂-Reduktion durch effizientes Fahren",
    "Echtzeit-Feedback für Fahrer",
    "Gamification-Elemente für Motivation",
    "Vergleichbare Leistungswerte",
    "Umweltbewusstes Fahren fördern"
  ];

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/user_manuals')}
        sx={{ mb: 3 }}
      >
        Zurück zum Handbuch
      </Button>

      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SensorsIcon sx={{ fontSize: 40, color: '#E30613', mr: 2 }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ color: '#E30613' }}>
              Telematik und EcoApp Revolution
            </Typography>
            <Typography variant="h6" color="text.secondary">
              2015-heute: Digitale Transformation des Busverkehrs
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
          Mit der Einführung von Telematik-Systemen und der innovativen EcoApp begann bei 
          DB Regio Bus Mitte eine neue Ära der digitalen Transformation. Diese Technologien 
          revolutionierten nicht nur die Art, wie Busse betrieben werden, sondern auch 
          wie Fahrer ihren Arbeitsalltag erleben.
        </Typography>

        {/* Telematik Section */}
        <Card sx={{ my: 4, backgroundColor: '#f0f8ff' }}>
          <CardContent>
            <Typography variant="h5" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CloudIcon />
              Was ist Telematik?
            </Typography>
            <Typography variant="body1" paragraph>
              Telematik kombiniert Telekommunikation und Informatik, um Fahrzeugdaten 
              in Echtzeit zu übertragen und zu analysieren. Bei DB Regio Bus Mitte wird 
              diese Technologie seit 2015 erfolgreich eingesetzt.
            </Typography>
            
            <Grid container spacing={2} sx={{ mt: 2 }}>
              {telematikFeatures.map((feature, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    {feature.icon}
                    <Box>
                      <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* EcoApp Section */}
        <Card sx={{ my: 4, backgroundColor: '#f0fff0' }}>
          <CardContent>
            <Typography variant="h5" gutterBottom color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmartphoneIcon />
              Die EcoApp - Nachhaltigkeit im Fokus
            </Typography>
            <Typography variant="body1" paragraph>
              Die EcoApp ist eine speziell entwickelte Anwendung, die Busfahrer dabei 
              unterstützt, umweltbewusst und kraftstoffsparend zu fahren. Seit ihrer 
              Einführung 2018 ist sie zu einem unverzichtbaren Werkzeug geworden.
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Hauptfunktionen der EcoApp:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {ecoAppBenefits.map((benefit, index) => (
                <Chip 
                  key={index}
                  label={benefit}
                  color="success"
                  variant="outlined"
                  size="small"
                />
              ))}
            </Stack>
          </CardContent>
        </Card>

        {/* Impact Section */}
        <Typography variant="h5" gutterBottom sx={{ color: '#E30613', mt: 4 }}>
          Auswirkungen auf den Betrieb
        </Typography>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#fff3e0' }}>
              <Typography variant="h3" color="warning.main" sx={{ fontWeight: 'bold' }}>
                -15%
              </Typography>
              <Typography variant="body1">
                Kraftstoffverbrauch reduziert
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#e8f5e8' }}>
              <Typography variant="h3" color="success.main" sx={{ fontWeight: 'bold' }}>
                +25%
              </Typography>
              <Typography variant="body1">
                Effizienz gesteigert
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ textAlign: 'center', p: 2, backgroundColor: '#e3f2fd' }}>
              <Typography variant="h3" color="primary" sx={{ fontWeight: 'bold' }}>
                98%
              </Typography>
              <Typography variant="body1">
                Fahrzeugverfügbarkeit
              </Typography>
            </Card>
          </Grid>
        </Grid>

        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, mt: 4 }}>
          Die Einführung von Telematik und EcoApp hat nicht nur messbare Verbesserungen 
          bei Effizienz und Umweltschutz gebracht, sondern auch die Arbeitszufriedenheit 
          der Fahrer erhöht. Durch präzises Feedback und spielerische Elemente wird 
          nachhaltiges Fahren gefördert und belohnt.
        </Typography>

        <Card sx={{ my: 3, backgroundColor: '#f5f5f5' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Zukunftsausblick
            </Typography>
            <Typography variant="body1">
              • Integration von KI-gestützten Prognosemodellen<br />
              • Erweiterte Predictive Maintenance-Funktionen<br />
              • Verbesserung der Fahrgastinformation durch Echtzeitdaten<br />
              • Weitere Optimierung der Routenplanung<br />
              • Integration erneuerbarer Energien bei Elektrobussen
            </Typography>
          </CardContent>
        </Card>

        <Divider sx={{ my: 3 }} />

        <Card sx={{ backgroundColor: '#e3f2fd' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Quellen:
            </Typography>
            <Typography variant="body2" component="div">
              • DB Regio AG: "Digitalisierung im ÖPNV" - Technischer Bericht (2020)<br />
              • Fraunhofer-Institut: "Telematik in Flottenbetrieben" - Studie (2019)<br />
              • VDV-Mitteilungen: "EcoApps im Busverkehr" - Fachzeitschrift (2021)<br />
              • Interne Evaluationsberichte DB Regio Bus Mitte (2018-2024)<br />
              • Umweltbundesamt: "CO₂-Reduktion durch intelligente Verkehrssysteme" (2022)
            </Typography>
          </CardContent>
        </Card>
      </Paper>
    </Container>
  );
};

export default ManualTelematik;